import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, Check, Copy, History as HistoryIcon, X } from 'lucide-react';
import clsx from 'clsx';
import {
  AUTO_LANG,
  effectiveInputTokens,
  estimateTokensPerTranslate,
  estimateTranslatesLeft,
  type ButtonHotkey,
  type TranslateButtonView,
} from '@translate-ai/shared';
import type { HistoryItem } from '@translate-ai/shared';
import { LangSelect } from '@/components/LangSelect';
import { HistoryItemCard } from '@/components/HistoryItemCard';
import {
  useDeleteTranslation,
  useHistory,
  useMe,
  useToggleFavorite,
  useTranslate,
  useTranslateConfig,
  useTranslateStream,
  useUpdateSettings,
  useUsage,
} from '@/api/hooks';
import { ApiError } from '@/api/client';
import { ErrorMessage } from '@/components/ErrorMessage';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useUILocale } from '@/hooks/useUILocale';
import { formatDurationSeconds } from '@/lib/duration';
import { localizeLabel } from '@/lib/labels';

function hotkeyHint(hotkey: ButtonHotkey | null): string {
  if (!hotkey) return '';
  if (hotkey === 'enter') return '↵ Enter';
  return '⌘/Ctrl + ↵';
}

export function Translate() {
  const { t } = useTranslation();
  const locale = useUILocale();
  const isDesktop = useIsDesktop();
  const me = useMe();
  const settings = me.data?.settings;
  const activeLanguages = settings?.activeLanguages;
  const updateSettings = useUpdateSettings();

  const [sourceLang, setSourceLang] = useState<string>(AUTO_LANG);
  const [targetLang, setTargetLang] = useState<string>('en');
  const [text, setText] = useState<string>('');
  const [result, setResult] = useState<string>('');
  const [resultMeta, setResultMeta] = useState<{
    durationMs: number;
    modelTitle: string;
  } | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const { copied, copy } = useCopyToClipboard();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!settings) return;
    setSourceLang(settings.lastSourceLang);
    setTargetLang(settings.lastTargetLang);
  }, [settings]);

  const navigate = useNavigate();
  const location = useLocation();
  const [pendingUseAsInput, setPendingUseAsInput] = useState<HistoryItem | null>(
    () => (location.state as { useAsInput?: HistoryItem } | null)?.useAsInput ?? null,
  );
  const translate = useTranslate();
  const stream = useTranslateStream();
  // True from click until the result is final (covers both the non-streaming
  // mutation and the streaming hook awaiting/receiving tokens).
  const isPending = translate.isPending || stream.isStreaming;
  const translateConfig = useTranslateConfig();
  const buttons = translateConfig.data?.buttons ?? [];
  const checkboxes = translateConfig.data?.checkboxes ?? [];
  const baseButton = buttons.find((b) => b.isBase) ?? null;
  const extraButtons = buttons.filter((b) => !b.isBase);

  const [pendingButtonId, setPendingButtonId] = useState<string | null>(null);

  const [enabledCheckboxIds, setEnabledCheckboxIds] = useState<string[]>([]);
  useEffect(() => {
    const valid = new Set(checkboxes.map((o) => o.id));
    setEnabledCheckboxIds((prev) => {
      const filtered = prev.filter((id) => valid.has(id));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [checkboxes]);

  const [recentFavoritesOnly, setRecentFavoritesOnly] = useState(false);
  const recent = useHistory(10, recentFavoritesOnly);
  const fav = useToggleFavorite();
  const del = useDeleteTranslation();

  const targetInActive = !activeLanguages || activeLanguages.includes(targetLang);
  const sourceInActive =
    sourceLang === AUTO_LANG || !activeLanguages || activeLanguages.includes(sourceLang);
  const canSwap = sourceLang !== AUTO_LANG && sourceInActive && targetInActive;
  const sameLangError =
    sourceLang !== AUTO_LANG && sourceLang === targetLang ? t('translate.sameLang') : null;

  // Usage estimation is keyed off the base button — switching to a different
  // button only changes the system prompt slightly, but using base keeps the
  // panel stable while typing.
  const usage = useUsage(baseButton?.id ?? null, enabledCheckboxIds);
  const usageData = usage.data ?? me.data?.usage;
  const remaining = usageData?.remainingTokensToday ?? 0;
  const quotaExceeded = remaining <= 0;

  const deferredText = useDeferredValue(text);
  const tokensPerTranslate = useMemo(() => {
    if (!usageData) return 0;
    return estimateTokensPerTranslate({
      systemPromptTokens: usageData.systemPromptTokens,
      inputTokens: effectiveInputTokens(deferredText),
      framingOverhead: usageData.framingOverhead,
    });
  }, [usageData?.systemPromptTokens, usageData?.framingOverhead, deferredText]);
  const translatesLeft = estimateTranslatesLeft(remaining, tokensPerTranslate);

  const errorMessage = useMemo(() => {
    if (!error) return null;
    if (error.code === 'QUOTA_EXCEEDED') return t('translate.quotaExceeded');
    return error.message || t('errors.generic');
  }, [error, t]);

  const saveLangs = (source: string, target: string) => {
    if (source !== AUTO_LANG && source === target) return;
    updateSettings.mutate({ lastSourceLang: source, lastTargetLang: target });
  };

  const handleSourceChange = (v: string) => {
    setSourceLang(v);
    saveLangs(v, targetLang);
  };

  const handleTargetChange = (v: string) => {
    setTargetLang(v);
    saveLangs(sourceLang, v);
  };

  const handleTranslate = (buttonId: string | null) => {
    if (!text.trim() || sameLangError || quotaExceeded) return;
    setResult('');
    setResultMeta(null);
    setError(null);
    const id = buttonId ?? baseButton?.id ?? null;
    setPendingButtonId(id);

    const body = {
      sourceLang,
      targetLang,
      text: text.trim(),
      enabledCheckboxIds,
      ...(buttonId ? { buttonId } : {}),
    };

    // The button's stored flag (from /translate/config) decides the transport:
    // streaming buttons use the SSE endpoint, the rest the plain JSON one.
    const button = buttons.find((b) => b.id === id) ?? baseButton;
    if (button?.isStreaming) {
      stream.start(body, {
        onToken: (delta) => setResult((prev) => prev + delta),
        onDone: (meta) => {
          setResultMeta({ durationMs: meta.durationMs, modelTitle: meta.modelTitle });
          setPendingButtonId(null);
        },
        onError: (e) => {
          setError(e);
          setPendingButtonId(null);
        },
      });
      return;
    }

    translate.mutate(body, {
      onSuccess: (r) => {
        setResult(r.translation);
        setResultMeta({ durationMs: r.durationMs, modelTitle: r.modelTitle });
      },
      onError: (e) => {
        setError(e as ApiError);
      },
      onSettled: () => {
        setPendingButtonId(null);
      },
    });
  };

  const handleSwap = () => {
    if (!canSwap) return;
    stream.cancel();
    const newSource = targetLang;
    const newTarget = sourceLang;
    setSourceLang(newSource);
    setTargetLang(newTarget);
    saveLangs(newSource, newTarget);
    if (result) {
      setText(result);
      setResult(text);
    }
    setResultMeta(null);
    setError(null);
  };

  const handleClear = () => {
    stream.cancel();
    setText('');
    setResult('');
    setResultMeta(null);
    setError(null);
    textareaRef.current?.focus();
  };

  const handleRecentClick = (item: HistoryItem) => {
    stream.cancel();
    setError(null);
    const reconciledSource =
      item.sourceLang === AUTO_LANG || !activeLanguages || activeLanguages.includes(item.sourceLang)
        ? item.sourceLang
        : AUTO_LANG;
    const reconciledTarget =
      !activeLanguages || activeLanguages.includes(item.targetLang)
        ? item.targetLang
        : activeLanguages[0] ?? targetLang;
    setText(item.sourceText);
    setSourceLang(reconciledSource);
    setTargetLang(reconciledTarget);
    setResult(item.translatedText);
    setResultMeta(null);
    saveLangs(reconciledSource, reconciledTarget);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    textareaRef.current?.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (!pendingUseAsInput || !settings) return;
    handleRecentClick(pendingUseAsInput);
    setPendingUseAsInput(null);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingUseAsInput, settings]);

  const handleCopy = async () => {
    if (!result) return;
    await copy(result);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleTranslate(baseButton?.id ?? null);
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!isDesktop) return;
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    const mod = e.metaKey || e.ctrlKey;
    const wanted: ButtonHotkey = mod ? 'mod_enter' : 'enter';
    const target = buttons.find((b) => b.hotkey === wanted);
    if (!target) return;
    e.preventDefault();
    handleTranslate(target.id);
  };

  const recentItems = recent.data?.pages.flatMap((p) => p.items) ?? [];

  const buttonLabel = (b: TranslateButtonView): string =>
    localizeLabel(b.name, locale) || t('translate.submit');

  return (
    <div className="flex flex-col gap-4 py-2">
      <div className="flex items-center gap-2">
        <LangSelect
          value={sourceLang}
          onChange={handleSourceChange}
          allowAuto
          codes={activeLanguages}
          aria-label="Source language"
          className="flex-1"
        />
        <button
          className="btn-ghost p-2"
          onClick={handleSwap}
          disabled={!canSwap}
          aria-label={t('translate.swap')}
        >
          <ArrowLeftRight className="h-5 w-5" />
        </button>
        <LangSelect
          value={targetLang}
          onChange={handleTargetChange}
          codes={activeLanguages}
          aria-label="Target language"
          className="flex-1"
        />
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="relative">
          <textarea
            ref={textareaRef}
            className="input min-h-[140px] resize-y pr-10"
            placeholder={t('translate.sourcePlaceholder')}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              if (stream.isStreaming) stream.cancel();
              if (result) setResult('');
              setResultMeta(null);
              if (error) setError(null);
            }}
            onKeyDown={handleTextareaKeyDown}
            maxLength={5000}
          />
          {text.length > 0 && (
            <button
              type="button"
              onClick={handleClear}
              aria-label={t('translate.clear')}
              title={isDesktop ? t('translate.clear') : undefined}
              className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus-visible:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {checkboxes.length ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {checkboxes.map((opt) => {
              const label = localizeLabel(opt.labels, locale);
              if (!label) return null;
              return (
                <label
                  key={opt.id}
                  className="flex items-center gap-2 text-sm text-slate-700"
                >
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    checked={enabledCheckboxIds.includes(opt.id)}
                    onChange={(e) =>
                      setEnabledCheckboxIds((prev) =>
                        e.target.checked
                          ? [...prev, opt.id]
                          : prev.filter((x) => x !== opt.id),
                      )
                    }
                  />
                  {label}
                </label>
              );
            })}
          </div>
        ) : null}

        <ErrorMessage message={sameLangError} />
        <ErrorMessage message={errorMessage} />

        <div className="space-y-2">
          {baseButton && (
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={!text.trim() || isPending || !!sameLangError || quotaExceeded}
            >
              {pendingButtonId === baseButton.id
                ? t('translate.submitting')
                : buttonLabel(baseButton)}
              {isDesktop && pendingButtonId !== baseButton.id && baseButton.hotkey && (
                <span className="ml-2 text-xs opacity-75">{hotkeyHint(baseButton.hotkey)}</span>
              )}
            </button>
          )}
          {extraButtons.map((b) => (
            <button
              key={b.id}
              type="button"
              className="btn-secondary w-full"
              disabled={!text.trim() || isPending || !!sameLangError || quotaExceeded}
              onClick={() => handleTranslate(b.id)}
            >
              {pendingButtonId === b.id ? t('translate.submitting') : buttonLabel(b)}
              {isDesktop && pendingButtonId !== b.id && b.hotkey && (
                <span className="ml-2 text-xs opacity-75">{hotkeyHint(b.hotkey)}</span>
              )}
            </button>
          ))}
        </div>
      </form>

      {isPending && !result ? (
        <div className="card p-4" role="status" aria-live="polite">
          <span className="sr-only">{t('app.loading')}</span>
          {stream.isStreaming && (
            <p className="mb-2 text-xs text-slate-400">{t('translate.thinking')}</p>
          )}
          <div className="space-y-2">
            <div className="h-4 w-full animate-pulse rounded bg-slate-200" />
            <div className="h-4 w-5/6 animate-pulse rounded bg-slate-200" />
            <div className="h-4 w-3/5 animate-pulse rounded bg-slate-200" />
          </div>
        </div>
      ) : result ? (
        <div>
          <div className="card group relative border-2 border-brand-500 p-4 shadow-md">
            <p className="whitespace-pre-wrap pr-12 text-slate-900">{result}</p>
            <button
              type="button"
              onClick={handleCopy}
              title={isDesktop ? t('translate.copy') : undefined}
              aria-label={t('translate.copy')}
              className={clsx(
                'absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 transition-opacity hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:opacity-100',
                isDesktop ? 'opacity-0 group-hover:opacity-100' : 'opacity-100',
              )}
            >
              {copied ? (
                <Check className="h-4 w-4 text-emerald-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
          {resultMeta && (
            <p className="mt-2 mr-3 text-right text-xs text-slate-400">
              {resultMeta.modelTitle && (
                <>
                  {resultMeta.modelTitle}
                  <span className="mx-1">·</span>
                </>
              )}
              {t('translate.modelDuration', {
                seconds: formatDurationSeconds(resultMeta.durationMs),
              })}
            </p>
          )}
        </div>
      ) : null}

      <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-slate-500">
        <span>
          {t('translate.remainingTokens', {
            count: remaining,
            translates: translatesLeft,
            perTranslate: tokensPerTranslate,
          })}
        </span>
      </div>

      <section aria-labelledby="recent-title">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 id="recent-title" className="text-sm font-medium text-slate-700">
            {t('translate.recentTitle')}
          </h2>
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              checked={recentFavoritesOnly}
              onChange={(e) => setRecentFavoritesOnly(e.target.checked)}
            />
            {t('translate.recentFavoritesOnly')}
          </label>
        </div>
        {recentItems.length === 0 ? (
          <p className="text-sm text-slate-500">{t('translate.empty')}</p>
        ) : (
          <ul className="space-y-2">
            {recentItems.slice(0, 10).map((item) => (
              <li key={item.id}>
                <HistoryItemCard
                  item={item}
                  onLoadIntoForm={() => handleRecentClick(item)}
                  onToggleFavorite={() =>
                    fav.mutate({
                      translationId: item.id,
                      isFavorite: !item.isFavorite,
                    })
                  }
                  onOpenLists={() => navigate(`/items/${item.id}/lists`)}
                  onDelete={() => {
                    if (window.confirm(t('lists.deleteTranslationConfirm'))) {
                      del.mutate(item.id);
                    }
                  }}
                />
              </li>
            ))}
          </ul>
        )}
        {recent.hasNextPage && (
          <button
            className="btn-secondary mt-3 w-full"
            onClick={() => navigate('/history')}
          >
            <HistoryIcon className="h-4 w-4" />
            {t('lists.showFullHistory')}
          </button>
        )}
      </section>
    </div>
  );
}
